import { Box, Card, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { ArrowUpRightIcon } from '@/components/icons/ArrowUpRightIcon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { getDisplayNameFromEntityType } from '@/modules/entities/utils/getDisplayNameFromEntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { getGstStatusDisplay } from '@/modules/entities/utils/getGstStatusDisplay';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import {
  ContextPrimaryClient,
  useHouseholdDetailsContext,
} from '@/modules/household/contexts/householdDetails.context';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';

import {
  getClientOwnershipPercentage,
  sumHoldingsGroupCurrentValue,
} from '../../../../DirectHoldingsGroupContainer.utils';

export function TrustOrBusiness({
  holdingsGroup,
  clientProfile,
}: {
  holdingsGroup: EstatePage_EntityFragment;
  clientProfile: ContextPrimaryClient;
}) {
  const currentValue = sumHoldingsGroupCurrentValue(
    [holdingsGroup],
    [clientProfile.id]
  );
  const navigate = useNavigate();
  const { householdId } = useHouseholdDetailsContext();

  const ownershipPercentage = getClientOwnershipPercentage(
    holdingsGroup,
    clientProfile.id
  );

  const handleNavigateToEntity = () => {
    navigate(
      getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS, {
        entityId: holdingsGroup.id,
        householdId: householdId!,
      })
    );
  };

  return (
    <Card
      variant="outlined"
      component="button"
      key={`holdings-group-${holdingsGroup.id}`}
      sx={{
        cursor: 'pointer',
        textAlign: 'left',
        ':focus-visible': {
          outline: 'solid black 2px',
          outlineOffset: 2,
        },
      }}
      onClick={handleNavigateToEntity}
    >
      <Stack
        p={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack flex={2}>
          <Stack direction="row">
            <Stack direction="row" alignItems="center" gap={1} flex={1}>
              <TypographyClamped
                variant="title-s"
                lines={1}
                sx={{ maxWidth: '75%' }}
              >
                {holdingsGroup.subtype.displayName}
              </TypographyClamped>

              <Badge
                sx={{ whiteSpace: 'nowrap' }}
                variant={BadgeVariants.SecondaryOutline}
                display={getDisplayNameFromEntityType(
                  getEntityTypeFromEntityKind(holdingsGroup.kind)
                )}
              />
            </Stack>
          </Stack>
          {'gstStatus' in holdingsGroup.subtype &&
            holdingsGroup.subtype.gstStatus && (
              <Typography variant="body-xs">
                {getGstStatusDisplay({
                  gstStatus: holdingsGroup.subtype.gstStatus,
                })}
              </Typography>
            )}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          sx={{ ml: 1 }}
        >
          <Stack>
            <Typography variant="title-s">
              {formatCurrencyNoDecimals(currentValue)}
            </Typography>
            {!!ownershipPercentage && (
              <Typography variant="body-xs">
                {formatPercent(ownershipPercentage, 2)}% ownership
              </Typography>
            )}
          </Stack>
          <Box
            sx={{
              padding: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArrowUpRightIcon size={16} color={COLORS.TEXT.SECONDARY} />
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
}
